import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { appendShopIdToFormData, buildShopScopedPath, resolveUserShopId } from '../../../utils/adminShopContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Branding logo requirements
const BRANDING_REQUIRED_WIDTH = 240;
const BRANDING_REQUIRED_HEIGHT = 72;
const BRANDING_MAX_FILE_BYTES = 25 * 1024 * 1024; // 25MB
// No longer enforcing max dimensions - backend will auto-resize to 500×500

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const CustomLogoSettings = () => {
  const { isDark } = useTheme();
  const { user, shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const isBrandingAdmin = !!user && ['shop_admin', 'superadmin'].includes(user.role);
  const effectiveShopId = shopId || resolveUserShopId(user);
  const fileInputRef = useRef(null);
  
  const [brandingLogo, setBrandingLogo] = useState({ url: null, version: null });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [brandingResetting, setBrandingResetting] = useState(false);
  const [brandingError, setBrandingError] = useState('');
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');

  const dispatchBrandingLogoUpdate = useCallback((url, version) => {
    try {
      window.dispatchEvent(
        new CustomEvent('branding:logo-updated', {
          detail: { url: url || null, version: version ?? null },
        })
      );
    } catch (_) {
      // best-effort
    }
  }, []);

  const loadBrandingLogo = useCallback(async () => {
    if (!isBrandingAdmin || !effectiveShopId) return;
    try {
      setBrandingLoading(true);
      const resp = await axiosClient.get(buildShopScopedPath('/admin/settings/branding/logo', effectiveShopId));
      setBrandingLogo({
        url: resp.data?.customLogoUrl || null,
        version: resp.data?.version ?? null,
      });
    } catch (error) {
      console.error('Error loading branding logo:', error);
      // Non-blocking
    } finally {
      setBrandingLoading(false);
    }
  }, [effectiveShopId, isBrandingAdmin]);

  const revokePreviewUrl = useCallback((url) => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        // ignore
      }
    }
  }, []);

  const validateLogoFile = async (file) => {
    // NOTE: SVG is intentionally NOT supported for logos because it commonly triggers
    // proxy 413 errors (large inline assets) and varies in rendering. Use PNG/JPG/WebP.
    const allowedMimes = new Set(['image/png', 'image/jpeg', 'image/webp']);
    const allowedExt = new Set(['.png', '.jpg', '.jpeg', '.webp']);

    const name = file?.name || '';
    const ext = name.includes('.') ? `.${name.split('.').pop().toLowerCase()}` : '';
    if (!allowedMimes.has(file.type) && !allowedExt.has(ext)) {
      return {
        ok: false,
        message: t('admin.settings_logo_invalid_format'),
      };
    }

    if (typeof file.size === 'number' && file.size > BRANDING_MAX_FILE_BYTES) {
      const mb = Math.ceil(BRANDING_MAX_FILE_BYTES / (1024 * 1024));
      return {
        ok: false,
        message: t('admin.settings_logo_too_large', { mb }),
      };
    }

    // Basic file validation - just ensure it's a valid image
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Invalid image'));
        img.src = objectUrl;
      });

      // No dimension restrictions - backend will auto-resize to 500×500
      return { ok: true, message: '' };
    } finally {
      revokePreviewUrl(objectUrl);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setBrandingError('');

    setSelectedLogoFile(null);
    setLogoPreviewUrl((prev) => {
      revokePreviewUrl(prev);
      return '';
    });

    if (!file) return;

    try {
      const validation = await validateLogoFile(file);
      if (!validation.ok) {
        setBrandingError(validation.message);
        toast.error(validation.message);
        e.target.value = '';
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setSelectedLogoFile(file);
      setLogoPreviewUrl(previewUrl);
    } catch (error) {
      console.error('Logo validation error:', error);
      const msg = t('admin.settings_logo_read_error');
      setBrandingError(msg);
      toast.error(msg);
      e.target.value = '';
    }
  };

  const handleSaveLogo = async () => {
    if (!selectedLogoFile) return;

    try {
      setBrandingUploading(true);
      setBrandingError('');

      const form = new FormData();
      form.append('logo', selectedLogoFile);
      appendShopIdToFormData(form, effectiveShopId);

      const resp = await axiosClient.post(buildShopScopedPath('/admin/settings/branding/logo', effectiveShopId), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBrandingLogo({
        url: resp.data?.customLogoUrl || null,
        version: resp.data?.version ?? null,
      });

      setSelectedLogoFile(null);
      setLogoPreviewUrl((prev) => {
        revokePreviewUrl(prev);
        return '';
      });

      toast.success(t('admin.settings_logo_saved'));
      dispatchBrandingLogoUpdate(resp.data?.customLogoUrl || null, resp.data?.version ?? null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      const status = error.response?.status;
      const errorMessage =
        status === 413
          ? t('admin.settings_logo_upload_too_large')
          : (error.response?.data?.message || t('admin.settings_logo_error'));
      setBrandingError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setBrandingUploading(false);
    }
  };

  const handleResetLogo = async () => {
    try {
      setBrandingResetting(true);
      setBrandingError('');
      const resp = await axiosClient.delete(buildShopScopedPath('/admin/settings/branding/logo', effectiveShopId));

      setBrandingLogo({ url: null, version: null });
      setSelectedLogoFile(null);
      setLogoPreviewUrl((prev) => {
        revokePreviewUrl(prev);
        return '';
      });

      toast.success(t('admin.settings_logo_reset'));
      dispatchBrandingLogoUpdate(resp.data?.customLogoUrl || null, resp.data?.version ?? null);
    } catch (error) {
      console.error('Error resetting logo:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_logo_reset_error');
      setBrandingError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setBrandingResetting(false);
    }
  };

  useEffect(() => {
    loadBrandingLogo();
  }, [loadBrandingLogo]);

  useEffect(() => {
    return () => {
      revokePreviewUrl(logoPreviewUrl);
    };
  }, [logoPreviewUrl, revokePreviewUrl]);

  if (!isBrandingAdmin) {
    return (
      <div className="p-6 text-center">
        <p className="text-zinc-600 dark:text-zinc-400">
          {t('admin.settings_logo_no_permission')}
        </p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('admin.settings_logo_title')}
        subtitle={t('admin.settings_logo_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 17h16M6 7v10m12-10v10" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-white/30 dark:bg-zinc-800/30 backdrop-blur-sm border border-white/20 dark:border-white/10">
              <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                {t('admin.settings_logo_requirements')}
              </div>
              <ul className="text-sm text-zinc-700 dark:text-zinc-300 space-y-1">
                <li><span className="font-medium">{t('admin.settings_logo_formats')}</span></li>
                <li><span className="font-medium">{t('admin.settings_logo_recommended')}</span></li>
                <li><span className="font-medium">{t('admin.settings_logo_auto')}</span></li>
                <li><span className="font-medium">{t('admin.settings_logo_transparent')}</span></li>
                <li><span className="font-medium">{t('admin.settings_logo_no_svg')}</span></li>
                <li><span className="font-medium">{t('admin.settings_logo_size_limit')}</span></li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.settings_upload_logo')}
              </label>
              {/* Hidden native file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                onChange={handleLogoFileChange}
                className="hidden"
                aria-label={t('admin.settings_upload_logo')}
              />
              <div className="flex items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-[--accent]/10 text-sm font-semibold transition-all"
                  style={{ '--accent': ACCENT, color: ACCENT }}
                >
                  {t('admin.qr_choose_file')}
                </motion.button>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">
                  {selectedLogoFile ? selectedLogoFile.name : t('admin.qr_no_file_chosen')}
                </span>
              </div>
              <p className="text-xs mt-2 text-zinc-500 dark:text-zinc-400">
                {t('admin.settings_logo_hint')}
              </p>
              {brandingError && (
                <p className="text-sm mt-2 text-red-600 dark:text-red-400">
                  {brandingError}
                </p>
              )}
              {!brandingError && brandingLoading && (
                <p className="text-xs mt-2 text-zinc-500 dark:text-zinc-400">{t('admin.settings_logo_loading')}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {t('admin.settings_logo_preview')}
            </div>
            <div
              className="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/40 backdrop-blur-sm flex items-center justify-center"
              style={{ maxWidth: BRANDING_REQUIRED_WIDTH, maxHeight: BRANDING_REQUIRED_HEIGHT, width: BRANDING_REQUIRED_WIDTH, height: BRANDING_REQUIRED_HEIGHT }}
            >
              {(() => {
                const persisted = brandingLogo.url
                  ? `${brandingLogo.url}${brandingLogo.version ? `?v=${brandingLogo.version}` : ''}`
                  : '';
                const src = logoPreviewUrl || persisted;
                if (!src) {
                  return (
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {t('admin.settings_logo_no_custom')}
                    </span>
                  );
                }
                return (
                  <img
                    src={src}
                    alt={t('admin.settings_logo_preview')}
                    style={{
                      maxWidth: BRANDING_REQUIRED_WIDTH,
                      maxHeight: BRANDING_REQUIRED_HEIGHT,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      objectPosition: 'left center',
                    }}
                  />
                );
              })()}
            </div>

            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              {t('admin.settings_logo_preview_hint')}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 px-4 md:px-5 pb-4 md:pb-5 pt-4 border-t border-white/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSaveLogo}
            disabled={brandingUploading || !selectedLogoFile}
            className="flex-1 sm:flex-initial px-4 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: ACCENT }}
          >
            {brandingUploading ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                <span>{t('admin.settings_saving_logo')}</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('admin.settings_save_logo')}</span>
              </>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleResetLogo}
            disabled={brandingResetting || brandingLoading || (!brandingLogo.url && !logoPreviewUrl && !selectedLogoFile)}
            className="flex-1 sm:flex-initial px-4 py-2.5 text-sm rounded-lg font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border"
            style={{
              background: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)',
              color: isDark ? '#fca5a5' : '#dc2626',
            }}
          >
            {brandingResetting ? (
              <>
                <CircularProgress size={16} style={{ color: isDark ? '#fca5a5' : '#dc2626' }} />
                <span>{t('admin.settings_resetting')}</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{t('admin.settings_reset_to_default')}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

export default CustomLogoSettings;
